<template>
  <div class="timesheet-card">
    <b-card>
      <b-card-title title-tag="h2" class="mb-5">
        TIMESHEET
      </b-card-title>

      <b-row class="mb-5">
        <b-col
          cols="12"
          lg="6"
          class="mb-0 mb-sm-4 mb-lg-0"
        >
          <ListDetails
            :values="employeeDetails"
            :options="employeeDetailsOptions"
          />
        </b-col>

        <b-col cols="12" lg="5" offset-lg="1" v-if="hasOtherDetails">
          <ListDetails
            :values="otherDetails"
            :options="otherDetailsOptions"
          />
        </b-col>
      </b-row>

      <b-col cols="12" class="mt-2">
        <div class="d-flex justify-content-center justify-content-md-end">
          <b-button
            v-if="csvBtn"
            squared
            variant="success"
            size="sm"
            class="mr-1"
            :disabled="csvBtnDisabled"
            @click="csvClick"
          >
            <v-icon name="file-csv" />
            <span>Export as CSV</span>
          </b-button>
          <b-button
            v-if="!noRefresh"
            squared
            variant="outline-primary"
            size="md"
            :disabled="refreshBtnDisabled"
            @click="fetchData"
          >
            <v-icon name="sync" />
            <span>Refresh <span class="d-none d-sm-inline">Timesheet Data</span></span>
          </b-button>
        </div>
      </b-col>

      <Datatable
        class="mt-4"
        :resources="resources"
        :options="datatableOptions"
        @row-clicked="onRowClick"
      >
        <!-- REMARKS -->
        <template v-slot:cell(remarks)="props">
          <template
            v-if="Array.isArray(props.item.remarks)"
          >
            <template v-if="props.item.remarks.length === 0">-</template>
            <template
              v-else
              v-for="(rm, idx) in props.item.remarks"
            >
              <span :key="`sp-${idx}`"> {{ rm || '-' }} </span>
              <br v-if="(idx+1) < props.item.remarks.length" :key="`br-${idx}`" />
            </template>
          </template>
          <template v-else>
            <span>{{ $get(props, 'item.remarks', '-') || '-' }}</span>
          </template>
        </template>
        <!-- DETAILS -->
        <template v-slot:row-details="row">
          <b-card>
              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right"><b>SHIFT SCHEDULE:</b></b-col>
                <b-col>{{ $get(row, 'item.shiftSchedule.str', '') }}</b-col>
                <b-col sm="3" class="text-sm-right"><b>DAY OF WEEK:</b></b-col>
                <b-col>{{ $get(row, 'item.dayOfWeek', '') }}</b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right"><b>HOURS WORKED:</b></b-col>
                <b-col>{{ Number($get(row, 'item.hoursWorked', '')).toFixed(2) }}</b-col>
              </b-row>
          </b-card>
        </template>
      </Datatable>

      <b-col
        v-if="!noSubmit"
        cols="12"
        md="6"
        offset-md="3"
      >
        <b-button
          squared
          size="lg"
          block
          :disabled="!enableSubmitBtn || isModalShowing"
          @click="showConfirmSubmit"
        >
          SUBMIT FOR APPROVAL
        </b-button>
      </b-col>
    </b-card>
    <Modal
      v-if="!noSubmit"
      v-bind.sync="confirmModalOptions"
      @okBtn="submitBtn"
    >
      <template v-if="isTimesheetSubmitEnabled" slot="default">
        <h4>Are you sure you want to submit the timesheet?</h4>
        <span>
          Your timesheet will be up for approval
          of your immediate superior or designated approver.
        </span>
      </template>
      <template v-else slot="default">
        <h4>Timesheet submission is disabled until further notice.</h4>
      </template>
    </Modal>
  </div>
</template>
<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import ListDetails from '@/components/ListDetails';
import Datatable from '@/components/Datatable';
import Modal from '@/components/Modal';

const BREAKPOINT = 768;

export default {
  name: 'TimesheetCard',
  components: {
    ListDetails,
    Datatable,
    Modal,
  },
  props: {
    csvBtn: {
      type: Boolean,
      default: false,
    },
    noRefresh: {
      type: Boolean,
      default: false,
    },
    noSubmit: {
      type: Boolean,
      default: false,
    },
    timesheetData: {
      type: Array,
      default: () => ([]),
    },
    loadingEmployee: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: () => ({}),
    },
    otherDetails: {
      type: Object,
      default: () => ({}),
    },
    otherDetailsOptions: {
      type: Object,
      default: () => ({}),
    },
    enableSubmitBtn: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    window.onresize = this.onResize;
  },
  data() {
    return {
      resourcesData: [],
      isSubmit: false,
      enableDetails: window.innerWidth < BREAKPOINT,
      confirmModalOptions: {
        show: false,
      },
    };
  },
  computed: {
    resources: {
      get() {
        return this.$ie(this.resourcesData)
          ? this.timesheetData.map((row, idx) => Object.assign({}, row, { idx }))
          : this.resourcesData;
      },
      set(v) {
        this.resourcesData = v;
      },
    },
    refreshBtnDisabled() {
      return this.isLoadingPage || this.isLoadingRequest || this.loadingEmployee;
    },
    csvBtnDisabled() {
      return this.isLoadingPage || this.isLoadingRequest || this.loadingEmployee;
    },
    employeeFields() {
      const defaultOptions = {
        size: 'sm',
        readonly: true,
        modifiers: ['text-left-align'],
        options: {
          labelColsSm: 4,
          labelCols: 12,
        },
      };
      const fieldNames = ['ID Number', 'Name', 'Position', 'Department', 'Team'];
      return fieldNames.map((field) => {
        let key = this.$snake(field);
        switch (field) {
          case 'ID Number':
            key = 'employee_no';
            break;
          case 'Name':
            key = 'full_name';
            break;
          default: break;
        }
        return {
          ...defaultOptions, label: field, value: '', key,
        };
      });
    },
    employeeDetails() {
      const details = {};
      this.employeeFields.forEach((field) => {
        const detailsToShow = [
          'full_name',
          'employee_no',
          'department',
          'team',
          'position',
        ];
        if (detailsToShow.includes(field.key)) {
          if (this.loadingEmployee) {
            details[field.key] = 'loading user data...';
          } else {
            details[field.key] = this.employee[field.key] || '';
          }
        }
      });
      return details;
    },
    employeeDetailsOptions() {
      return {
        labelClass: 'font-weight-bold',
        fields: this.employeeFields,
      };
    },
    tableFields() {
      return [
        {
          key: 'shiftSchedule',
          label: 'Shift Schedule',
          sortable: false,
          class: 'd-none d-md-table-cell',
          formatter: ss => this.$get(ss, 'str', null),
        },
        {
          key: 'dayOfWeek',
          label: 'Day of the Week',
          sortable: false,
          class: 'd-none d-md-table-cell',
          formatter: dayName => (dayName ? this.$title(dayName) : '-'),
        },
        {
          key: 'date',
          label: 'Date',
          sortable: false,
          formatter: val => this.$fdr(val),
          class: 'text-center',
        },
        {
          key: 'timeIn',
          label: 'Time In',
          sortable: false,
          class: 'text-center',
          formatter: (val, key, item) => {
            let rawValueToDisplay = val;

            const obData = this.$get(item, 'ob', null);
            // has ob
            if (!this.$ie(obData)) {
              const obStartTime = this.$get(obData, 'start_time', null);
              // if no time in
              if (this.$ie(val)) {
                // use ob start time by default
                rawValueToDisplay = obStartTime;
              } else {
                // if time in is earlier than ob start time
                const useTimeIn = this.$diffMin(rawValueToDisplay, obStartTime) < 0;

                // ob start time is earlier than time in
                if (!useTimeIn) {
                  rawValueToDisplay = obStartTime;
                }
              }
            }
            // has overtime
            const overtimeData = this.$get(item, 'overtime', null);
            if (!this.$ie(overtimeData)) {
              const overtimeStartTime = this.$get(overtimeData, 'start_time', null);
              // if no time in and no OB
              if (this.$ie(val) && this.$ie(item.ob)) {
                // use ob start time by default
                rawValueToDisplay = overtimeStartTime;
              } else {
                // if earliest raw value(from above) is earlier than overtime start time
                const useTimeIn = this.$diffMin(rawValueToDisplay, overtimeStartTime) < 0;

                // overtime start time is earlier than earliest raw value(from above)
                if (!useTimeIn) {
                  rawValueToDisplay = overtimeStartTime;
                }
              }
            }
            return this.$ie(rawValueToDisplay) ? '--:--' : this.$fto(rawValueToDisplay);
          },
        },
        {
          key: 'timeOut',
          label: 'Time Out',
          sortable: false,
          class: 'text-center',
          formatter: (val, key, item) => {
            let rawValueToDisplay = val;

            // has OB
            const obData = this.$get(item, 'ob', null);
            if (!this.$ie(obData)) {
              const obEndTime = this.$get(obData, 'end_time', null);
              // if no time out
              if (this.$ie(val)) {
                rawValueToDisplay = obEndTime;
              } else {
                // if time out is later than ob end time
                const useTimeOut = this.$diffMin(obEndTime, rawValueToDisplay) < 0;

                if (!useTimeOut) {
                  rawValueToDisplay = obEndTime;
                }
              }
            }

            // has overtime
            const overtimeData = this.$get(item, 'overtime', null);
            if (!this.$ie(overtimeData)) {
              const overtimeEndTime = this.$get(overtimeData, 'end_time', null);
              // if no time out
              if (this.$ie(val) && this.$ie(obData)) {
                rawValueToDisplay = overtimeEndTime;
              } else {
                // if time out is later than ob end time
                const useTimeOut = this.$diffMin(overtimeEndTime, rawValueToDisplay) < 0;
                // user overtime end time
                if (!useTimeOut) {
                  rawValueToDisplay = overtimeEndTime;
                }
              }
            }
            return this.$ie(rawValueToDisplay) ? '--:--' : this.$fto(rawValueToDisplay);
          },
        },
        {
          key: 'hoursWorked',
          label: 'Total Hours Worked',
          sortable: false,
          formatter: val => (Number(val) ? val.toFixed(2) : '-'),
          class: 'text-center d-none d-md-table-cell',
        },
        {
          key: 'remarks',
          label: 'Remarks',
          sortable: false,
          thClass: 'text-center',
          tdClass: (value, key, item) => {
            let cellClass = '';
            if (item.noLogs) {
              cellClass = 'bg-danger text-white font-weight-bold';
            } else if (item.isRestDay || item.noTimeOut || item.isOB) {
              cellClass = 'font-weight-bold';
            }
            return `text-center ${cellClass}`;
          },
        },
      ];
    },
    datatableOptions() {
      const otherOptions = {
        small: true,
        pagination: false,
        fields: this.tableFields || [],
        loading: this.isLoadingRequest && !this.isSubmit,
        trClass: this.rowClass,
        perPage: 16,
      };
      return Object.assign({}, this.options, otherOptions);
    },
    hasOtherDetails() {
      return !this.$ie(this.otherDetails);
    },
    ...mapState(['notification']),
    ...mapGetters([
      'isLoadingPage',
      'isLoadingRequest',
      'isModalShowing',
      'isTimesheetSubmitEnabled',
    ]),
  },
  watch: {
    isLoadingRequest(val) {
      if (!val) {
        this.isSubmit = false;
      }
    },
  },
  methods: {
    onResize() {
      this.enableDetails = window.innerWidth < BREAKPOINT;
      // hide all details
      if (!this.enableDetails) {
        this.resources = this.resources.map(
          row => Object.assign({}, row, { _showDetails: false }),
        );
      }
    },
    /**
     * Show confirmation dialog asking user if sure to submit timesheet for approval
     */
    showConfirmSubmit() {
      const title = this.isTimesheetSubmitEnabled ? 'SUBMIT TIMESHEET' : 'TIMESHEET DISABLED';
      this.confirmModalOptions = {
        title,
        show: true,
        cancelBtnOptions: {
          variant: 'outline-secondary',
        },
        okBtnOptions: {
          variant: 'primary',
          // text: 'OK, Submit my Timesheet',
          text: 'OK',
        },
      };
    },
    submitBtn() {
      this.isSubmit = true;
      this.$emit('submitBtn');
    },
    rowClass(item) {
      const isRestDay = this.$get(item, 'isRestDay', false);
      const isLeave = this.$get(item, 'isLeave', false);
      const isOB = this.$get(item, 'isOB', false);
      if (isOB) {
        return 'table-warning';
      }
      if (isRestDay) {
        return 'table-success';
      }
      if (isLeave) {
        // TODO: different classes for different leave types
        return 'leave';
      }
      return '';
    },
    showConfirmDelete() {
      this.confirmDelete.show = true;
    },
    fetchData() {
      this.$emit('fetchData');
    },
    csvClick() {
      this.$emit('csvClick');
    },
    onRowClick(payload) {
      if (!this.enableDetails) {
        return;
      }
      const idx = this.$get(payload, 'idx', null);
      if (idx !== null) {
        /* eslint-disable no-underscore-dangle */
        const newState = !this.resources[idx]._showDetails;
        // show row details (only 1 at a time)
        this.resources = this.resources.map((row, rowIdx) => {
          if (idx === rowIdx) {
            return Object.assign({}, row, { _showDetails: newState });
          }
          return Object.assign({}, row, { _showDetails: false });
        });
        /* eslint-enable */
      }
    },
    ...mapActions(['setNotification']),
  },
};
</script>
<style lang="scss" scoped>
.timesheet-card {
  .card {
    .card-title {
      text-align: center;
    }
  }
}
</style>
